// Rozszerzona baza materiałów do obliczeń wytrzymałościowych
// Wszystkie wartości w jednostkach SI

import { logModuleInit } from '@/utils/invariant';
logModuleInit('ExtendedMaterialDatabase.ts');

export interface MaterialProperties {
  name: string;
  displayName: string;
  category: 'thermoplastic' | 'thermoset' | 'glass' | 'metal' | 'composite' | 'wood';
  density: number; // kg/m³
  modulusOfElasticity: number; // MPa (E - moduł Younga)
  tensileStrength: number; // MPa (wytrzymałość na rozciąganie)
  flexuralStrength: number; // MPa (wytrzymałość na zginanie)
  compressiveStrength: number; // MPa (wytrzymałość na ściskanie)
  shearStrength: number; // MPa (wytrzymałość na ścinanie)
  elongationAtBreak: number; // % (wydłużenie przy zerwaniu)
  poissonsRatio: number; // współczynnik Poissona
  hardness: string; // różne skale (Shore, Rockwell, Brinell)
  maxServiceTemp: number; // °C
  minServiceTemp: number; // °C
  thermalExpansion: number; // 10^-6/K (współczynnik rozszerzalności cieplnej)
  thermalConductivity: number; // W/(m·K)
  specificHeat: number; // J/(kg·K)
  waterAbsorption24h: number; // % wagowy
  impactStrength: number; // kJ/m² (udarność)
  dielectricStrength: number; // kV/mm (wytrzymałość dielektryczna)
  surfaceResistivity: number; // Ω (rezystywność powierzchniowa)
  transmittance: number; // % (przepuszczalność światła)
  refractiveIndex: number; // współczynnik załamania światła
  uvResistance: boolean;
  chemicalResistance: 'low' | 'medium' | 'high' | 'excellent';
  fireRating: string; // różne standardy (UL94, EN 13501)
  foodSafe: boolean;
  recyclable: boolean;
  pricePerKg: number; // PLN/kg
  applications: string[];
  advantages: string[];
  disadvantages: string[];
  processingTemp: number; // °C (temperatura przetwórstwa)
  moldShrinkage: number; // % (skurcz formowania)
}

// === ROZSZERZONA BAZA MATERIAŁÓW ===
export const EXTENDED_MATERIALS: Record<string, MaterialProperties> = {
  // === PMMA (Plexiglas/Akryl) ===
  pmma_xt: {
    name: 'pmma_xt',
    displayName: 'PMMA XT (Plexi ekstrudowane)',
    category: 'thermoplastic',
    density: 1190,
    modulusOfElasticity: 3300,
    tensileStrength: 72,
    flexuralStrength: 115,
    compressiveStrength: 124,
    shearStrength: 75,
    elongationAtBreak: 4.5,
    poissonsRatio: 0.37,
    hardness: 'M95 (Rockwell)',
    maxServiceTemp: 80,
    minServiceTemp: -40,
    thermalExpansion: 70,
    thermalConductivity: 0.19,
    specificHeat: 1450,
    waterAbsorption24h: 0.30,
    impactStrength: 15,
    dielectricStrength: 20,
    surfaceResistivity: 1e15,
    transmittance: 92,
    refractiveIndex: 1.491,
    uvResistance: true,
    chemicalResistance: 'medium',
    fireRating: 'HB (UL94)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 30,
    applications: ['Przeszklenia', 'Osłony', 'Gabloty', 'Reklama'],
    advantages: ['Przezroczyste', 'Tanie', 'Łatwe w obróbce'],
    disadvantages: ['Kruche', 'Wrażliwe na zarysowania', 'Palny'],
    processingTemp: 150,
    moldShrinkage: 0.5
  },

  pmma_gs: {
    name: 'pmma_gs',
    displayName: 'PMMA GS (Plexi lite/odlewane)',
    category: 'thermoplastic',
    density: 1190,
    modulusOfElasticity: 3200,
    tensileStrength: 80,
    flexuralStrength: 110,
    compressiveStrength: 130,
    shearStrength: 80,
    elongationAtBreak: 5.5,
    poissonsRatio: 0.37,
    hardness: 'M100 (Rockwell)',
    maxServiceTemp: 85,
    minServiceTemp: -40,
    thermalExpansion: 67,
    thermalConductivity: 0.19,
    specificHeat: 1450,
    waterAbsorption24h: 0.25,
    impactStrength: 18,
    dielectricStrength: 22,
    surfaceResistivity: 1e15,
    transmittance: 92,
    refractiveIndex: 1.491,
    uvResistance: true,
    chemicalResistance: 'medium',
    fireRating: 'HB (UL94)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 35,
    applications: ['Akwaria', 'Szyby lotnicze', 'Meble', 'Premium gabloty'],
    advantages: ['Najlepsza jakość optyczna', 'Stabilne wymiarowo', 'Łatwe polerowanie'],
    disadvantages: ['Droższe od XT', 'Kruche', 'Wymaga ostrożnej obróbki termicznej'],
    processingTemp: 160,
    moldShrinkage: 0.4
  },

  pmma_impact: {
    name: 'pmma_impact',
    displayName: 'PMMA Impact (Plexi odporne na uderzenia)',
    category: 'thermoplastic',
    density: 1160,
    modulusOfElasticity: 2400,
    tensileStrength: 55,
    flexuralStrength: 80,
    compressiveStrength: 95,
    shearStrength: 60,
    elongationAtBreak: 25,
    poissonsRatio: 0.37,
    hardness: 'M65 (Rockwell)',
    maxServiceTemp: 70,
    minServiceTemp: -40,
    thermalExpansion: 75,
    thermalConductivity: 0.18,
    specificHeat: 1500,
    waterAbsorption24h: 0.35,
    impactStrength: 65,
    dielectricStrength: 18,
    surfaceResistivity: 1e15,
    transmittance: 89,
    refractiveIndex: 1.491,
    uvResistance: true,
    chemicalResistance: 'medium',
    fireRating: 'HB (UL94)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 45,
    applications: ['Osłony bezpieczeństwa', 'Szyby antywłamaniowe', 'Sport'],
    advantages: ['Wysokа odporność na uderzenia', 'Trudne do stłuczenia', 'Elastyczne'],
    disadvantages: ['Niższa przezroczystość', 'Miększe', 'Łatwiej się rysuje'],
    processingTemp: 140,
    moldShrinkage: 0.6
  },

  // === PETG ===
  petg_clear: {
    name: 'petg_clear',
    displayName: 'PETG (Glikol politereftalanu etylenu)',
    category: 'thermoplastic',
    density: 1270,
    modulusOfElasticity: 2100,
    tensileStrength: 50,
    flexuralStrength: 70,
    compressiveStrength: 65,
    shearStrength: 45,
    elongationAtBreak: 120,
    poissonsRatio: 0.38,
    hardness: 'R105 (Rockwell)',
    maxServiceTemp: 70,
    minServiceTemp: -40,
    thermalExpansion: 65,
    thermalConductivity: 0.29,
    specificHeat: 1200,
    waterAbsorption24h: 0.13,
    impactStrength: 85,
    dielectricStrength: 17,
    surfaceResistivity: 1e14,
    transmittance: 90,
    refractiveIndex: 1.57,
    uvResistance: false,
    chemicalResistance: 'high',
    fireRating: 'HB (UL94)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 25,
    applications: ['Osłony medyczne', 'Opakowania', 'Displays POS'],
    advantages: ['Bardzo odporne na uderzenia', 'Chemicznie odporne', 'Łatwe termoformowanie'],
    disadvantages: ['Miękkie', 'Żółknie na UV', 'Łatwo się rysuje'],
    processingTemp: 260,
    moldShrinkage: 0.4
  },

  // === Poliwęglan ===
  polycarbonate_clear: {
    name: 'polycarbonate_clear',
    displayName: 'Poliwęglan lity (PC)',
    category: 'thermoplastic',
    density: 1200,
    modulusOfElasticity: 2400,
    tensileStrength: 63,
    flexuralStrength: 90,
    compressiveStrength: 85,
    shearStrength: 60,
    elongationAtBreak: 100,
    poissonsRatio: 0.37,
    hardness: 'M70 (Rockwell)',
    maxServiceTemp: 130,
    minServiceTemp: -135,
    thermalExpansion: 65,
    thermalConductivity: 0.21,
    specificHeat: 1170,
    waterAbsorption24h: 0.15,
    impactStrength: 850,
    dielectricStrength: 30,
    surfaceResistivity: 1e15,
    transmittance: 89,
    refractiveIndex: 1.586,
    uvResistance: true,
    chemicalResistance: 'medium',
    fireRating: 'V-2 (UL94)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 50,
    applications: ['Szyby kuloodporne', 'Kaski', 'Elektronika', 'Automotive'],
    advantages: ['Niewiarygodnie wytrzymały', 'Szeroki zakres temperatur', 'Niepalny'],
    disadvantages: ['Drogi', 'Żółknie', 'Wrażliwy na chemikalia'],
    processingTemp: 280,
    moldShrinkage: 0.8
  },

  polycarbonate_compact: {
    name: 'polycarbonate_compact',
    displayName: 'Poliwęglan komorowy',
    category: 'thermoplastic',
    density: 800,
    modulusOfElasticity: 1800,
    tensileStrength: 60,
    flexuralStrength: 75,
    compressiveStrength: 70,
    shearStrength: 50,
    elongationAtBreak: 80,
    poissonsRatio: 0.37,
    hardness: 'M65 (Rockwell)',
    maxServiceTemp: 120,
    minServiceTemp: -40,
    thermalExpansion: 67,
    thermalConductivity: 0.21,
    specificHeat: 1200,
    waterAbsorption24h: 0.20,
    impactStrength: 200,
    dielectricStrength: 20,
    surfaceResistivity: 1e15,
    transmittance: 82,
    refractiveIndex: 1.586,
    uvResistance: true,
    chemicalResistance: 'low',
    fireRating: 'B-s1,d0 (EN 13501)',
    foodSafe: false,
    recyclable: true,
    pricePerKg: 25,
    applications: ['Zadaszenia', 'Świetliki', 'Przegrody', 'Szklarnie'],
    advantages: ['Lekkie', 'Izolacja termiczna', 'Wytrzymałe'],
    disadvantages: ['Rozszerzalność termiczna', 'Wymaga profili montażowych'],
    processingTemp: 280,
    moldShrinkage: 0.8
  },

  // === PVC ===
  pvc_foam_3mm: {
    name: 'pvc_foam_3mm',
    displayName: 'PVC spienione 3mm (Forex®)',
    category: 'thermoplastic',
    density: 500,
    modulusOfElasticity: 600,
    tensileStrength: 12,
    flexuralStrength: 15,
    compressiveStrength: 8,
    shearStrength: 6,
    elongationAtBreak: 20,
    poissonsRatio: 0.30,
    hardness: 'Shore D 45',
    maxServiceTemp: 60,
    minServiceTemp: -20,
    thermalExpansion: 70,
    thermalConductivity: 0.075,
    specificHeat: 1000,
    waterAbsorption24h: 0.04,
    impactStrength: 5,
    dielectricStrength: 20,
    surfaceResistivity: 1e13,
    transmittance: 0,
    refractiveIndex: 1.54,
    uvResistance: true,
    chemicalResistance: 'high',
    fireRating: 'B1 (DIN 4102)',
    foodSafe: false,
    recyclable: true,
    pricePerKg: 18,
    applications: ['Reklama', 'Wystawiennictwo', 'Zabudowy'],
    advantages: ['Lekkie', 'Łatwe w obróbce', 'Odporne na wilgoć'],
    disadvantages: ['Miękkie', 'Wrażliwe na temperaturę', 'Kruche na mrozie'],
    processingTemp: 160,
    moldShrinkage: 0.5
  },

  pvc_foam_5mm: {
    name: 'pvc_foam_5mm',
    displayName: 'PVC spienione 5mm (Forex®)',
    category: 'thermoplastic',
    density: 500,
    modulusOfElasticity: 600,
    tensileStrength: 12,
    flexuralStrength: 15,
    compressiveStrength: 8,
    shearStrength: 6,
    elongationAtBreak: 20,
    poissonsRatio: 0.30,
    hardness: 'Shore D 45',
    maxServiceTemp: 60,
    minServiceTemp: -20,
    thermalExpansion: 70,
    thermalConductivity: 0.075,
    specificHeat: 1000,
    waterAbsorption24h: 0.04,
    impactStrength: 5,
    dielectricStrength: 20,
    surfaceResistivity: 1e13,
    transmittance: 0,
    refractiveIndex: 1.54,
    uvResistance: true,
    chemicalResistance: 'high',
    fireRating: 'B1 (DIN 4102)',
    foodSafe: false,
    recyclable: true,
    pricePerKg: 18,
    applications: ['Reklama', 'Wystawiennictwo', 'Modele'],
    advantages: ['Lekkie', 'Sztywniejsze niż 3mm', 'Dobre do druku'],
    disadvantages: ['Miękkie', 'Wrażliwe na UV długoterminowo'],
    processingTemp: 160,
    moldShrinkage: 0.5
  },

  pvc_foam_10mm: {
    name: 'pvc_foam_10mm',
    displayName: 'PVC spienione 10mm (Forex®)',
    category: 'thermoplastic',
    density: 450,
    modulusOfElasticity: 500,
    tensileStrength: 10,
    flexuralStrength: 12,
    compressiveStrength: 6,
    shearStrength: 5,
    elongationAtBreak: 15,
    poissonsRatio: 0.30,
    hardness: 'Shore D 40',
    maxServiceTemp: 60,
    minServiceTemp: -20,
    thermalExpansion: 70,
    thermalConductivity: 0.075,
    specificHeat: 1000,
    waterAbsorption24h: 0.05,
    impactStrength: 4,
    dielectricStrength: 18,
    surfaceResistivity: 1e13,
    transmittance: 0,
    refractiveIndex: 1.54,
    uvResistance: true,
    chemicalResistance: 'high',
    fireRating: 'B1 (DIN 4102)',
    foodSafe: false,
    recyclable: true,
    pricePerKg: 16,
    applications: ['Zabudowy', 'Ścianki działowe', 'Makiety'],
    advantages: ['Bardzo lekkie', 'Dobra izolacja', 'Sztywne'],
    disadvantages: ['Kruche', 'Trudniejsze w obróbce precyzyjnej'],
    processingTemp: 160,
    moldShrinkage: 0.5
  },

  // === HIPS ===
  hips_white: {
    name: 'hips_white',
    displayName: 'HIPS biały',
    category: 'thermoplastic',
    density: 1050,
    modulusOfElasticity: 2100,
    tensileStrength: 22,
    flexuralStrength: 35,
    compressiveStrength: 35,
    shearStrength: 20,
    elongationAtBreak: 40,
    poissonsRatio: 0.35,
    hardness: 'R80 (Rockwell)',
    maxServiceTemp: 70,
    minServiceTemp: -40,
    thermalExpansion: 80,
    thermalConductivity: 0.17,
    specificHeat: 1350,
    waterAbsorption24h: 0.05,
    impactStrength: 10,
    dielectricStrength: 20,
    surfaceResistivity: 1e13,
    transmittance: 0,
    refractiveIndex: 1.59,
    uvResistance: false,
    chemicalResistance: 'medium',
    fireRating: 'HB (UL94)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 15,
    applications: ['Lodówki', 'Termoformowanie', 'Zabawki', 'Modele'],
    advantages: ['Tanie', 'Łatwe termoformowanie', 'Dobre do druku'],
    disadvantages: ['Kruche', 'Brak UV', 'Niska wytrzymałość'],
    processingTemp: 200,
    moldShrinkage: 0.5
  },

  // === SAN ===
  san_clear: {
    name: 'san_clear',
    displayName: 'SAN (Styrene Acrylonitrile)',
    category: 'thermoplastic',
    density: 1080,
    modulusOfElasticity: 3600,
    tensileStrength: 70,
    flexuralStrength: 100,
    compressiveStrength: 90,
    shearStrength: 60,
    elongationAtBreak: 3,
    poissonsRatio: 0.35,
    hardness: 'M80 (Rockwell)',
    maxServiceTemp: 85,
    minServiceTemp: -20,
    thermalExpansion: 65,
    thermalConductivity: 0.17,
    specificHeat: 1400,
    waterAbsorption24h: 0.25,
    impactStrength: 2.5,
    dielectricStrength: 20,
    surfaceResistivity: 1e15,
    transmittance: 90,
    refractiveIndex: 1.57,
    uvResistance: false,
    chemicalResistance: 'high',
    fireRating: 'HB (UL94)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 25,
    applications: ['Artykuły gospodarstwa domowego', 'Kosmetyki', 'Medycyna'],
    advantages: ['Przezroczysty', 'Sztywny', 'Odporny chemicznie'],
    disadvantages: ['Kruchy', 'Wrażliwy na zarysowania', 'Brak UV'],
    processingTemp: 220,
    moldShrinkage: 0.4
  },

  // === Kompozyty ===
  dibond_3mm: {
    name: 'dibond_3mm',
    displayName: 'Dibond® 3mm',
    category: 'composite',
    density: 1800,
    modulusOfElasticity: 26000,
    tensileStrength: 120,
    flexuralStrength: 150,
    compressiveStrength: 140,
    shearStrength: 80,
    elongationAtBreak: 2,
    poissonsRatio: 0.33,
    hardness: 'HB (ołówek)',
    maxServiceTemp: 80,
    minServiceTemp: -50,
    thermalExpansion: 24,
    thermalConductivity: 0.3,
    specificHeat: 900,
    waterAbsorption24h: 0.02,
    impactStrength: 25,
    dielectricStrength: 40,
    surfaceResistivity: 1e12,
    transmittance: 0,
    refractiveIndex: 0,
    uvResistance: true,
    chemicalResistance: 'excellent',
    fireRating: 'A2 (EN 13501)',
    foodSafe: false,
    recyclable: true,
    pricePerKg: 45,
    applications: ['Fasady', 'Oznakowanie', 'Konstrukcje'],
    advantages: ['Płaskie', 'Sztywne', 'Odporne na warunki atmosferyczne'],
    disadvantages: ['Ciężkie', 'Trudne w gięciu', 'Widoczne krawędzie'],
    processingTemp: 0,
    moldShrinkage: 0
  },

  // === Szkło ===
  glass_float_4mm: {
    name: 'glass_float_4mm',
    displayName: 'Szkło float 4mm',
    category: 'glass',
    density: 2500,
    modulusOfElasticity: 70000,
    tensileStrength: 45,
    flexuralStrength: 120,
    compressiveStrength: 1000,
    shearStrength: 30,
    elongationAtBreak: 0,
    poissonsRatio: 0.22,
    hardness: '6 (Mohs)',
    maxServiceTemp: 200,
    minServiceTemp: -200,
    thermalExpansion: 9,
    thermalConductivity: 1.0,
    specificHeat: 840,
    waterAbsorption24h: 0,
    impactStrength: 2,
    dielectricStrength: 13,
    surfaceResistivity: 1e12,
    transmittance: 90,
    refractiveIndex: 1.52,
    uvResistance: true,
    chemicalResistance: 'excellent',
    fireRating: 'A1 (niepalne)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 10,
    applications: ['Okna', 'Lustra', 'Meble', 'Akwaria'],
    advantages: ['Przezroczyste', 'Twarde', 'Odporne chemicznie'],
    disadvantages: ['Kruche', 'Niebezpieczne przy pęknięciu', 'Ciężkie'],
    processingTemp: 600,
    moldShrinkage: 0
  },

  glass_tempered_6mm: {
    name: 'glass_tempered_6mm',
    displayName: 'Szkło hartowane 6mm',
    category: 'glass',
    density: 2500,
    modulusOfElasticity: 70000,
    tensileStrength: 120,
    flexuralStrength: 200,
    compressiveStrength: 1000,
    shearStrength: 60,
    elongationAtBreak: 0,
    poissonsRatio: 0.22,
    hardness: '6.5 (Mohs)',
    maxServiceTemp: 250,
    minServiceTemp: -200,
    thermalExpansion: 9,
    thermalConductivity: 1.0,
    specificHeat: 840,
    waterAbsorption24h: 0,
    impactStrength: 10,
    dielectricStrength: 13,
    surfaceResistivity: 1e12,
    transmittance: 90,
    refractiveIndex: 1.52,
    uvResistance: true,
    chemicalResistance: 'excellent',
    fireRating: 'A1 (niepalne)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 25,
    applications: ['Drzwi', 'Balustrady', 'Kabiny prysznicowe', 'Fasady'],
    advantages: ['5x mocniejsze od zwykłego', 'Bezpieczne pęknięcie', 'Odporne termicznie'],
    disadvantages: ['Nie można ciąć po hartowaniu', 'Droższe', 'Może mieć zniekształcenia'],
    processingTemp: 620,
    moldShrinkage: 0
  },

  glass_laminated_66: {
    name: 'glass_laminated_66',
    displayName: 'Szkło laminowane 6.6.2',
    category: 'glass',
    density: 2500,
    modulusOfElasticity: 70000,
    tensileStrength: 80,
    flexuralStrength: 150,
    compressiveStrength: 1000,
    shearStrength: 45,
    elongationAtBreak: 0,
    poissonsRatio: 0.22,
    hardness: '6 (Mohs)',
    maxServiceTemp: 100,
    minServiceTemp: -40,
    thermalExpansion: 9,
    thermalConductivity: 0.9,
    specificHeat: 840,
    waterAbsorption24h: 0,
    impactStrength: 30,
    dielectricStrength: 12,
    surfaceResistivity: 1e12,
    transmittance: 88,
    refractiveIndex: 1.52,
    uvResistance: true,
    chemicalResistance: 'excellent',
    fireRating: 'A1 (niepalne)',
    foodSafe: true,
    recyclable: false,
    pricePerKg: 35,
    applications: ['Szyby antywłamaniowe', 'Przeszklenia dachowe', 'Schody'],
    advantages: ['Bezpieczne', 'Tłumi dźwięk', 'Filtr UV'],
    disadvantages: ['Ciężkie', 'Grube', 'Droższe'],
    processingTemp: 0,
    moldShrinkage: 0
  },

  // === Metale (dla porównania) ===
  aluminum_6061: {
    name: 'aluminum_6061',
    displayName: 'Aluminium 6061-T6',
    category: 'metal',
    density: 2700,
    modulusOfElasticity: 69000,
    tensileStrength: 310,
    flexuralStrength: 280,
    compressiveStrength: 280,
    shearStrength: 185,
    elongationAtBreak: 12,
    poissonsRatio: 0.33,
    hardness: '95 (Brinell)',
    maxServiceTemp: 200,
    minServiceTemp: -200,
    thermalExpansion: 23.6,
    thermalConductivity: 167,
    specificHeat: 896,
    waterAbsorption24h: 0,
    impactStrength: 30,
    dielectricStrength: 0,
    surfaceResistivity: 2.65e-8,
    transmittance: 0,
    refractiveIndex: 0,
    uvResistance: true,
    chemicalResistance: 'medium',
    fireRating: 'A1 (niepalne)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 20,
    applications: ['Profile konstrukcyjne', 'Ramy', 'Obudowy'],
    advantages: ['Lekkie', 'Mocne', 'Odporne na korozję'],
    disadvantages: ['Przewodzi prąd', 'Rozszerza się termicznie', 'Miękkie'],
    processingTemp: 500,
    moldShrinkage: 0
  },

  stainless_steel_304: {
    name: 'stainless_steel_304',
    displayName: 'Stal nierdzewna 304',
    category: 'metal',
    density: 7930,
    modulusOfElasticity: 193000,
    tensileStrength: 515,
    flexuralStrength: 460,
    compressiveStrength: 500,
    shearStrength: 325,
    elongationAtBreak: 40,
    poissonsRatio: 0.29,
    hardness: '187 (Brinell)',
    maxServiceTemp: 870,
    minServiceTemp: -200,
    thermalExpansion: 17.3,
    thermalConductivity: 16.2,
    specificHeat: 500,
    waterAbsorption24h: 0,
    impactStrength: 100,
    dielectricStrength: 0,
    surfaceResistivity: 7.2e-7,
    transmittance: 0,
    refractiveIndex: 0,
    uvResistance: true,
    chemicalResistance: 'excellent',
    fireRating: 'A1 (niepalne)',
    foodSafe: true,
    recyclable: true,
    pricePerKg: 35,
    applications: ['Przemysł spożywczy', 'Medycyna', 'Architektura'],
    advantages: ['Niekorodująca', 'Higieniczna', 'Wytrzymała'],
    disadvantages: ['Ciężka', 'Droga', 'Trudna w obróbce'],
    processingTemp: 1400,
    moldShrinkage: 0
  },

  // === Drewno i płyty drewnopochodne ===
  mdf_18mm: {
    name: 'mdf_18mm',
    displayName: 'MDF 18mm',
    category: 'wood',
    density: 750,
    modulusOfElasticity: 4000,
    tensileStrength: 18,
    flexuralStrength: 23,
    compressiveStrength: 15,
    shearStrength: 8,
    elongationAtBreak: 0.5,
    poissonsRatio: 0.25,
    hardness: 'Shore D 60',
    maxServiceTemp: 80,
    minServiceTemp: -20,
    thermalExpansion: 12,
    thermalConductivity: 0.15,
    specificHeat: 1700,
    waterAbsorption24h: 10,
    impactStrength: 8,
    dielectricStrength: 3,
    surfaceResistivity: 1e10,
    transmittance: 0,
    refractiveIndex: 0,
    uvResistance: false,
    chemicalResistance: 'low',
    fireRating: 'D-s2,d0 (Euroklasa)',
    foodSafe: false,
    recyclable: false,
    pricePerKg: 3,
    applications: ['Meble', 'Zabudowy', 'Modele'],
    advantages: ['Tanie', 'Łatwe w obróbce', 'Gładka powierzchnia'],
    disadvantages: ['Pęcznieje od wilgoci', 'Ciężkie', 'Pylące przy cięciu'],
    processingTemp: 0,
    moldShrinkage: 0
  },

  plywood_birch_12mm: {
    name: 'plywood_birch_12mm',
    displayName: 'Sklejka brzozowa 12mm',
    category: 'wood',
    density: 700,
    modulusOfElasticity: 9000,
    tensileStrength: 40,
    flexuralStrength: 60,
    compressiveStrength: 35,
    shearStrength: 15,
    elongationAtBreak: 1,
    poissonsRatio: 0.30,
    hardness: 'Shore D 65',
    maxServiceTemp: 100,
    minServiceTemp: -40,
    thermalExpansion: 5,
    thermalConductivity: 0.13,
    specificHeat: 2500,
    waterAbsorption24h: 5,
    impactStrength: 15,
    dielectricStrength: 2,
    surfaceResistivity: 1e11,
    transmittance: 0,
    refractiveIndex: 0,
    uvResistance: false,
    chemicalResistance: 'low',
    fireRating: 'D-s2,d0 (Euroklasa)',
    foodSafe: false,
    recyclable: false,
    pricePerKg: 8,
    applications: ['Konstrukcje', 'Transport', 'Opakowania'],
    advantages: ['Mocna', 'Stabilna wymiarowo', 'Elastyczna'],
    disadvantages: ['Droższa od MDF', 'Warstwa może się rozwarstwić'],
    processingTemp: 0,
    moldShrinkage: 0
  }
};

// === MATERIAŁY PODSTAWOWE (kompatybilność wsteczna) ===
export const MATERIALS = EXTENDED_MATERIALS;

// Grubości standardowe dla materiałów (mm)
export const STANDARD_THICKNESSES: Record<string, number[]> = {
  pmma_xt: [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30],
  pmma_gs: [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50],
  pmma_impact: [3, 4, 5, 6, 8, 10, 12, 15, 20],
  petg_clear: [1, 2, 3, 4, 5, 6, 8, 10, 12, 15],
  polycarbonate_clear: [2, 3, 4, 5, 6, 8, 10, 12, 15, 20],
  polycarbonate_compact: [4, 6, 8, 10, 16, 20, 25, 32],
  pvc_foam_3mm: [3],
  pvc_foam_5mm: [5],
  pvc_foam_10mm: [10, 15, 19],
  hips_white: [1, 1.5, 2, 3, 4, 5],
  san_clear: [2, 3, 4, 5, 6, 8],
  dibond_3mm: [3, 4],
  glass_float_4mm: [4, 5, 6, 8, 10, 12],
  glass_tempered_6mm: [4, 5, 6, 8, 10, 12, 15, 19],
  glass_laminated_66: [6.6, 8.8, 10.10, 12.12],
  aluminum_6061: [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20],
  stainless_steel_304: [1, 1.5, 2, 3, 4, 5, 6, 8, 10],
  mdf_18mm: [3, 6, 10, 12, 16, 18, 22, 25, 30],
  plywood_birch_12mm: [4, 6, 9, 12, 15, 18, 21, 24, 27, 30]
};

// Współczynniki bezpieczeństwa dla różnych zastosowań
export const SAFETY_FACTORS = {
  display: 2.0, // ekspozycja, lekkie obciążenia
  storage: 3.0, // przechowywanie, półki
  structural: 4.0, // elementy konstrukcyjne
  public: 5.0, // przestrzenie publiczne
  industrial: 6.0, // zastosowania przemysłowe
  safety_critical: 10.0 // zastosowania krytyczne dla bezpieczeństwa
};

// Typy podparcia
export type SupportType = 
  | 'simply_supported' // podparte na dwóch końcach
  | 'cantilever' // wspornik
  | 'fixed_both' // utwierdzone na obu końcach
  | 'three_sides' // podparte na 3 bokach
  | 'four_sides' // podparte na 4 bokach
  | 'continuous' // ciągłe podparcie
  | 'point_supports'; // podparcie punktowe

// Współczynniki dla różnych typów podparcia
export const SUPPORT_COEFFICIENTS = {
  simply_supported: 0.125,
  cantilever: 1.0,
  fixed_both: 0.0208,
  three_sides: 0.04,
  four_sides: 0.028,
  continuous: 0.015,
  point_supports: 0.25
};

// Funkcje pomocnicze
export const getMaterial = (name: string): MaterialProperties | null => {
  return MATERIALS[name] || null;
};

export const getMaterialsByCategory = (category: string): MaterialProperties[] => {
  return Object.values(MATERIALS).filter(m => m.category === category);
};

export const findBestMaterial = (
  requirements: {
    minStrength?: number;
    maxWeight?: number;
    maxPrice?: number;
    transparent?: boolean;
    uvResistant?: boolean;
    foodSafe?: boolean;
    temperature?: number;
    chemicalResistance?: 'low' | 'medium' | 'high' | 'excellent';
  }
): string[] => {
  const suitable: string[] = [];
  
  Object.entries(MATERIALS).forEach(([key, material]) => {
    let matches = true;
    
    if (requirements.minStrength && material.flexuralStrength < requirements.minStrength) {
      matches = false;
    }
    if (requirements.maxWeight && material.density > requirements.maxWeight) {
      matches = false;
    }
    if (requirements.maxPrice && material.pricePerKg > requirements.maxPrice) {
      matches = false;
    }
    if (requirements.transparent && material.transmittance < 80) {
      matches = false;
    }
    if (requirements.uvResistant && !material.uvResistance) {
      matches = false;
    }
    if (requirements.foodSafe && !material.foodSafe) {
      matches = false;
    }
    if (requirements.temperature && 
        (material.maxServiceTemp < requirements.temperature || 
         material.minServiceTemp > requirements.temperature)) {
      matches = false;
    }
    if (requirements.chemicalResistance) {
      const resistanceLevels = { 'low': 1, 'medium': 2, 'high': 3, 'excellent': 4 };
      if (resistanceLevels[material.chemicalResistance] < resistanceLevels[requirements.chemicalResistance]) {
        matches = false;
      }
    }
    
    if (matches) {
      suitable.push(key);
    }
  });
  
  // Sortuj po cenie
  return suitable.sort((a, b) => MATERIALS[a].pricePerKg - MATERIALS[b].pricePerKg);
};